import { useState } from 'react';
import { CrossIcon, RefreshIcon } from 'assets/icons';
import Button from 'components/button';
import {
  fillText,
  monthLabel,
} from 'components/invoiceImport/invoiceImportText';
import useOpenInvoiceDetail from 'components/invoiceDetail/useOpenInvoiceDetail';
import PillTabs from 'components/tabs/PillTabs';
import UseContext from 'hooks/useContext';
import { titleMonth } from './invoiceJournalText';
import JournalErrorsTab from './JournalErrorsTab';
import JournalHistoryTab from './JournalHistoryTab';
import useInvoiceImportErrors from './useInvoiceImportErrors';
import useInvoiceImportRuns from './useInvoiceImportRuns';

export const JOURNAL_TABS = { toFix: 'toFix', history: 'history' };

/**
 * "Journal d'import · Bestt → Pennylane" of a month (JBWEB-1280): a panel on
 * the right, above the page like a SlideOver (z-30), under the PDF viewer
 * (z-50) and the import toast (z-60). It stands alone: the global SlideOver
 * registry is tied to the context and to the URL parameters.
 *
 * Mounted only while open, so that nothing is read before; `refreshKey`
 * changes when a run of the month starts or ends.
 */
const InvoiceImportJournal = ({
  period,
  initialTab = JOURNAL_TABS.toFix,
  refreshKey,
  onClose,
  onRetry,
  isRetryDisabled,
}) => {
  const { language } = UseContext();
  const openInvoice = useOpenInvoiceDetail();
  const text = (key, fallback) =>
    language.getValue(`invoiceJournal.${key}`, fallback);
  const [activeTab, setActiveTab] = useState(initialTab);
  const errors = useInvoiceImportErrors(period, refreshKey);
  const runs = useInvoiceImportRuns(period, refreshKey);
  const month = monthLabel(period);
  const errorsTotal = errors.data?.total;

  const tabs = [
    {
      id: JOURNAL_TABS.toFix,
      label: text('tabToFix', 'À corriger'),
      count: errorsTotal,
    },
    {
      id: JOURNAL_TABS.history,
      label: text('tabHistory', 'Historique'),
      count: runs.data?.total,
    },
  ];
  const isToFix = activeTab === JOURNAL_TABS.toFix;

  return (
    <div className="fixed inset-0 z-30" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900 opacity-50" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-import-journal-title"
        className="absolute right-0 h-full w-full lg:w-[600px] bg-white flex flex-col animate-slideR"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-5">
          <div>
            <div className="text-xs uppercase text-gray-500">
              {text('title', 'Journal d’import · Bestt → Pennylane')}
            </div>
            <h2
              id="invoice-import-journal-title"
              className="ft-lg font-medium text-gray-900"
            >
              {titleMonth(month)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={text('close', 'Fermer')}
            className="shrink-0 flex items-center justify-center w-9 h-9 rounded-md border border-gray-200 hover:bg-gray-50"
          >
            <CrossIcon wh={16} color="#374151" />
          </button>
        </div>
        <div className="px-5 pb-4">
          <PillTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5">
          {/* Keyed by tab so the fade replays on each switch; the two tabs are
              different components, so they remounted anyway. */}
          <div key={activeTab} className="motion-safe:animate-contentIn">
            {isToFix ? (
              <JournalErrorsTab
                errors={errors}
                text={text}
                month={month}
                onRetry={onRetry}
                isRetryDisabled={isRetryDisabled}
                onViewInvoice={(invoice) => openInvoice(invoice.number)}
              />
            ) : (
              <JournalHistoryTab
                runs={runs}
                text={text}
                onShowToFix={() => setActiveTab(JOURNAL_TABS.toFix)}
              />
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
          <Button color="SECONDARY" size="SM" onClick={onClose}>
            {text('close', 'Fermer')}
          </Button>
          {isToFix && errorsTotal > 0 && (
            <Button
              color="PRIMARY"
              size="SM"
              disabled={isRetryDisabled}
              onClick={() => onRetry()}
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshIcon wh={20} color="#FFFFFF" />
              {fillText(text('retryAll', 'Tout réessayer ({count})'), {
                count: errorsTotal,
              })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceImportJournal;
